import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPublicCourses, getPublicCourseTees, getPublicCourse, getPublicCourseLeaderboard, getPublicCourseLeaderboardMonthly } from '../api';
import { Spinner, ScoreBadge, RankBadge, AdPlaceholder, formatDate, Logo } from '../components/UI';

// ── Simple public navbar (no auth state needed) ───────────────────────────────
function PublicNav() {
  return (
    <nav className="bg-white/80 backdrop-blur-sm border-b border-amber-100 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo size="sm" />
          <span className="font-serif font-bold text-green-900 text-lg">GolfBoard Scores</span>
        </Link>
        <Link to="/login" className="btn-secondary text-sm">Sign In</Link>
      </div>
    </nav>
  );
}

// ── Course browser (shown when no teeId is selected yet) ─────────────────────
function PublicCourseBrowser() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [tees, setTees] = useState({});
  const [teesLoading, setTeesLoading] = useState(false);

  useEffect(() => {
    getPublicCourses()
      .then(data => {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setCourses(sorted);
        setFiltered(sorted);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      courses.filter(c =>
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.state?.toLowerCase().includes(q)
      )
    );
  }, [search, courses]);

  const handleExpandCourse = async (course) => {
    if (expandedCourse === course.id) { setExpandedCourse(null); return; }
    setExpandedCourse(course.id);
    if (tees[course.id]) return;
    setTeesLoading(true);
    try {
      const data = await getPublicCourseTees(course.id);
      setTees(prev => ({ ...prev, [course.id]: data }));
    } finally {
      setTeesLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-serif font-bold text-3xl text-green-900">Public Leaderboards</h1>
        <p className="text-gray-500 text-sm mt-1">
          Browse scores without an account. <Link to="/login" className="text-amber-700 font-medium hover:underline">Sign in</Link> to post your own score or verify others.
        </p>
      </div>

      {/* Top ad slot */}
      <AdPlaceholder className="mb-5 h-20" />

      {/* Search */}
      <div className="relative mb-5">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></span>
        <input
          type="text"
          className="input-field pl-9"
          placeholder="🔍 Search courses by name, city, or state..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No courses match your search.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(course => (
            <div key={course.id} className="card overflow-hidden">
              <button
                className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors"
                onClick={() => handleExpandCourse(course)}
              >
                <div>
                  <h3 className="font-serif font-semibold text-green-900 text-base">{course.name}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {[course.city, course.state, course.country].filter(Boolean).join(', ')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {teesLoading && expandedCourse === course.id && <Spinner size="sm" />}
                  <span className={`text-gray-400 text-sm transition-transform duration-200 ${expandedCourse === course.id ? 'rotate-180' : ''}`}>▾</span>
                </div>
              </button>

              {expandedCourse === course.id && tees[course.id] && (
                <div className="border-t border-amber-50 px-3 pb-3 pt-2 fade-in">
                  {/* Show available tees as info pills, then single CTA to course leaderboard */}
                  <div className="flex flex-wrap gap-1.5 px-3 py-2">
                    {(tees[course.id] || []).sort((a,b) => b.yardage - a.yardage).map(tee => (
                      <span key={tee.id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs rounded-full px-2 py-0.5">
                        <span className="w-2.5 h-2.5 rounded-full border border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: tee.color.toLowerCase() === 'white' ? '#f0f0f0' : tee.color.toLowerCase() }} />
                        <span className="capitalize">{tee.color}</span>
                        <span className="text-gray-300">·</span>
                        <span>{tee.yardage.toLocaleString()} yds</span>
                        <span className="text-gray-300">·</span>
                        <span>{tee.gender === 'male' ? 'M' : 'F'}</span>
                      </span>
                    ))}
                  </div>
                  <div
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-amber-50 cursor-pointer transition-colors group mt-1"
                    onClick={() => navigate(`/public/leaderboard/${course.id}`)}
                  >
                    <span className="text-sm font-medium text-green-800">View Course Leaderboard</span>
                    <span className="text-green-600 group-hover:translate-x-0.5 transition-transform text-sm">→</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bottom ad slot */}
      <AdPlaceholder className="mt-8 h-20" />

      {/* Sign-up CTA */}
      <div className="mt-8 card p-6 text-center bg-green-50 border-green-100">
        <h3 className="font-serif font-semibold text-green-900 text-lg mb-2">Ready to join the board?</h3>
        <p className="text-gray-500 text-sm mb-4">Create a free account to post scores, verify results, and compete with golfers worldwide.</p>
        <Link to="/login" className="btn-gold inline-block">Create Free Account</Link>
      </div>
    </div>
  );
}

// ── Public monthly leaderboard tab ───────────────────────────────────────────
function PublicMonthlyLeaderboard({ courseId }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2024; y--) yearOptions.push(y);

  useEffect(() => {
    setLoading(true);
    setError('');
    getPublicCourseLeaderboardMonthly(courseId, year, month)
      .then(data => setLeaderboard(data.data || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [courseId, year, month]);

  return (
    <div>
      {/* Month/Year filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <select
          className="input-field py-1.5 text-sm w-auto"
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          className="input-field py-1.5 text-sm w-auto"
          value={year}
          onChange={e => setYear(Number(e.target.value))}
        >
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-gray-400">Showing top scores for {MONTHS[month - 1]} {year}</span>
      </div>

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size="lg" /></div>
      ) : leaderboard.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">📅</div>
          <p className="font-medium">No scores for {MONTHS[month - 1]} {year}.</p>
          <p className="text-sm mt-1">
            <Link to="/login" className="text-amber-700 font-semibold underline">Sign in</Link> to be the first to post this month!
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry, idx) => (
            <div key={entry.score_id || idx} className="card p-4 flex items-center gap-3">
              <RankBadge rank={entry.place || idx + 1} />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-green-800 text-amber-100 flex items-center justify-center text-xs font-semibold border-2 border-amber-200 flex-shrink-0">
                  {entry.username?.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <span className="font-medium text-green-900 truncate block">{entry.username}</span>
                  {entry.tee_color && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs rounded-full px-2 py-0.5">
                      <span className="w-2.5 h-2.5 rounded-full border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: entry.tee_color.toLowerCase() === 'white' ? '#f0f0f0' : entry.tee_color.toLowerCase() }} />
                      <span className="capitalize">{entry.tee_color}</span>
                      <span className="text-gray-300">·</span>
                      <span>{entry.tee_yardage?.toLocaleString()} yds</span>
                      <span className="text-gray-300">·</span>
                      <span>{entry.tee_gender === 'male' ? 'M' : 'F'}</span>
                    </span>
                  )}
                  {entry.is_verified && (
                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 font-semibold rounded-full px-1.5 py-0.5 ml-1">
                      ✓ Verified
                    </span>
                  )}
                  <div className="text-xs text-gray-400 mt-0.5">{formatDate(entry.date_posted)}</div>
                  {entry.friend_post_status === 'pending_player' && (
                    <div className="inline-flex items-center gap-1 mt-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full px-2 py-0.5">
                      📤 Posted by friend · pending
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-serif font-bold text-lg text-green-900">{entry.total_score}</div>
                <ScoreBadge overUnder={entry.over_under_par} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Public tee leaderboard view ───────────────────────────────────────────────
function PublicCourseLeaderboard({ courseId }) {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alltime');

  useEffect(() => {
    Promise.all([getPublicCourse(courseId), getPublicCourseLeaderboard(courseId)])
      .then(([courseData, lbData]) => { setCourse(courseData); setLeaderboard(lbData); })
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!course) return <div className="text-center py-20 text-gray-400">Leaderboard not found.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Back */}
      <button
        onClick={() => navigate('/public/leaderboard/browse')}
        className="flex items-center gap-1 text-sm text-green-700 hover:text-green-900 mb-4 transition-colors"
      >
        ← All courses
      </button>

      {/* Course header */}
      <div className="mb-5">
        <h1 className="font-serif font-bold text-2xl text-green-900">{course?.name}</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {[course?.city, course?.state, course?.country].filter(Boolean).join(', ')}
        </p>
      </div>

      {/* Course info card */}
      <div className="card p-4 mb-5 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-400">All tees compete together · same score on same tee = tied</p>
        <Link to="/login" className="btn-gold flex-shrink-0 text-sm">Sign in to post</Link>
      </div>

      {/* Top ad slot */}
      <AdPlaceholder className="mb-5 h-20" />

      {/* Sign-in banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-amber-800">
          <span className="font-medium">Want to post a score or verify results?</span>{' '}
          <Link to="/login" className="underline font-semibold">Sign in or create a free account.</Link>
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('alltime')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-all ${
            activeTab === 'alltime' ? 'bg-white text-green-900 shadow-sm' : 'text-gray-500 hover:text-green-800'
          }`}
        >
          🏆 All-Time
        </button>
        <button
          onClick={() => setActiveTab('monthly')}
          className={`flex-1 text-sm font-medium py-2 rounded-lg transition-all ${
            activeTab === 'monthly' ? 'bg-white text-green-900 shadow-sm' : 'text-gray-500 hover:text-green-800'
          }`}
        >
          📅 Monthly
        </button>
      </div>

      {/* All-Time tab */}
      {activeTab === 'alltime' && (
        <>
          {leaderboard.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-2">🏌️</div>
              <p className="font-medium">No scores yet at this course.</p>
              <p className="text-sm mt-1"><Link to="/login" className="text-amber-700 font-semibold underline">Sign in</Link> to be the first!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.map(entry => (
                <div key={entry.score_id} className="card p-3 flex items-center gap-3">
                  <RankBadge rank={entry.place} />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-green-800 text-amber-100 flex items-center justify-center text-xs font-semibold border-2 border-amber-200 flex-shrink-0">
                      {entry.username?.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-green-900 truncate block text-sm">{entry.username}</span>
                      {entry.tee_color && (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs rounded-full px-2 py-0.5">
                          <span className="w-2.5 h-2.5 rounded-full border border-gray-300 flex-shrink-0"
                            style={{ backgroundColor: entry.tee_color.toLowerCase() === 'white' ? '#f0f0f0' : entry.tee_color.toLowerCase() }} />
                          <span className="capitalize">{entry.tee_color}</span>
                          <span className="text-gray-300">·</span>
                          <span>{entry.tee_yardage?.toLocaleString()} yds</span>
                          <span className="text-gray-300">·</span>
                          <span>{entry.tee_gender === 'male' ? 'M' : 'F'}</span>
                        </span>
                      )}
                      {entry.is_verified && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 font-semibold rounded-full px-1.5 py-0.5 ml-1">
                          ✓ Verified
                        </span>
                      )}
                      <div className="text-xs text-gray-400 mt-0.5">{formatDate(entry.date_posted)}</div>
                      {entry.friend_post_status === 'pending_player' && (
                        <div className="inline-flex items-center gap-1 mt-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full px-2 py-0.5">
                          📤 Posted by friend · pending
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-serif font-bold text-lg text-green-900">{entry.total_score}</div>
                    <ScoreBadge overUnder={entry.over_under_par} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {leaderboard.length > 0 && (
            <p className="text-center text-xs text-gray-400 mt-4">
              {leaderboard[leaderboard.length - 1]?.total_scores_on_course || leaderboard.length} total score{leaderboard.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}

      {/* Monthly tab */}
      {activeTab === 'monthly' && <PublicMonthlyLeaderboard courseId={courseId} />}

      {/* Bottom ad slot */}
      <AdPlaceholder className="mt-8 h-20" />

      {/* Sign-up CTA */}
      <div className="mt-6 card p-6 text-center bg-green-50 border-green-100">
        <h3 className="font-serif font-semibold text-green-900 text-lg mb-2">Join the leaderboard</h3>
        <p className="text-gray-500 text-sm mb-4">Create a free account to post your score at this course and compete with others.</p>
        <Link to="/login" className="btn-gold inline-block">Create Free Account</Link>
      </div>
    </div>
  );
}

// ── Main export: decides whether to show browser or specific tee ──────────────
export default function PublicLeaderboardPage() {
  const { teeId } = useParams();
  const isBrowse = !teeId || teeId === 'browse';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--cream)' }}>
      <PublicNav />
      <main className="flex-1">
        {isBrowse ? <PublicCourseBrowser /> : <PublicCourseLeaderboard courseId={teeId} />}
      </main>
      <footer className="bg-green-900 text-green-300 py-6 px-5 mt-auto">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-semibold text-amber-200">GolfBoard Scores</span>
          </div>
          <div className="flex gap-4 text-xs">
            <Link to="/" className="hover:text-amber-200">Home</Link>
            <Link to="/terms" className="hover:text-amber-200">Terms</Link>
            <Link to="/privacy" className="hover:text-amber-200">Privacy</Link>
            <Link to="/contact" className="hover:text-amber-200">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}